/**
 * This is the advanced version of "getDataGeneric"
 * It is more readable and uses fetch instead of axios and it is synchronous.
 */
import { _MESSAGES, _MEMBER, _SWAL_ICONS } from "../utils/constants";
import { HttpClient } from "../utils/httpClient";
import { createToast } from "../utils/toast";

/**
 * Generic data fetcher service that handles API requests and responses
 */
export class DataService {
  constructor(httpClient = null) {
    if (httpClient === null) {
      httpClient = new HttpClient();
    }

    this.httpClient = httpClient;
  }

  cleanupUrl(url) {
    return url.includes("api/") ? url.replace("api/", "") : url;
  }

  /**
   * Fetches data from a specified endpoint with error handling and data transformation
   * @param {string} route - API endpoint
   * @param {Object} params - Query parameters
   * @param {Function} onSuccess - Success callback function
   * @param {Object} options - Additional options
   * @returns {Promise<void>}
   */
  async fetchData(
    route,
    params = {},
    onSuccess = (data) => {},
    { errorMessage = _MESSAGES.error, transformResponse = true } = {},
  ) {
    try {
      const response = await this.httpClient.get(route, params);

      // Transform response if needed
      const data =
        transformResponse && response[_MEMBER] ? response[_MEMBER] : response;

      await onSuccess(data);
    } catch (error) {
      createToast(_SWAL_ICONS.error, errorMessage);
      console.error(`Error fetching data from ${route}:`, error);
      throw error;
    }
  }

  /**
   * Generic request handler for all HTTP methods
   * @param {string} method - HTTP method (GET, POST, PUT, PATCH, DELETE)
   * @param {string} url - API endpoint
   * @param {object} params - Request parameters or body
   * @param {object} options - Additional request options
   * @param {Function} onSuccess - Success callback function
   * @returns {Promise<any>} Response data
   */
  async doRequest(
    method,
    url,
    params = {},
    options = {},
    onSuccess = (response) => {},
  ) {
    try {
      // Clean up URL
      const cleanUrl = this.cleanupUrl(url);

      // Map HTTP methods to class methods
      const methodMap = {
        GET: () => this.httpClient.get(cleanUrl, params, options),
        POST: () => this.httpClient.post(cleanUrl, params, options),
        PUT: () => this.httpClient.put(cleanUrl, params, options),
        PATCH: () => this.httpClient.patch(cleanUrl, params, options),
        DELETE: () => this.httpClient.delete(cleanUrl, params, options),
        POSTFILE: () => this.httpClient.uploadFile(cleanUrl, params, options),
      };

      const requestMethod = methodMap[method.toUpperCase()];

      if (!requestMethod) {
        throw new Error(`Unsupported HTTP method: ${method}`);
      }

      const response = await requestMethod();

      await onSuccess(response);

      return response;
    } catch (error) {
      createToast(_SWAL_ICONS.error, _MESSAGES.error);
      console.error(`Request failed for ${method} ${url}:`, error);
      throw error;
    }
  }
}
